/**
 * ThinkingIndicator — Inline thinking display in the transcript stream
 *
 * Shows pulsing brain icon during active thinking, or a collapsible
 * summary block when thinking text is available.
 */
import { useState } from 'react'

interface ThinkingIndicatorProps {
    text?: string
    isActive: boolean
    startedAt?: number
    now?: number
}

function formatDuration(ms: number): string {
    if (ms < 1000) return `${Math.max(0, Math.round(ms))}ms`
    if (ms < 10_000) return `${(ms / 1000).toFixed(1)}s`
    return `${Math.round(ms / 1000)}s`
}

export function ThinkingIndicator({ text, isActive, startedAt, now }: ThinkingIndicatorProps) {
    const [expanded, setExpanded] = useState(false)
    const elapsedMs = startedAt ? Math.max(0, (now ?? Date.now()) - startedAt) : null
    const thoughtChars = text?.trim().length ?? 0

    if (!isActive && !text) return null

    return (
        <div className={`agent-thinking ${isActive ? 'thinking-active' : 'thinking-done'}`}>
            <div
                className="thinking-header"
                onClick={() => text && setExpanded(!expanded)}
                role={text ? 'button' : undefined}
                tabIndex={text ? 0 : undefined}
            >
                <span className={`thinking-icon ${isActive ? 'pulse' : ''}`}>🧠</span>
                <span className="thinking-label">
                    {isActive ? 'Thinking' : 'Reasoning'}
                </span>
                {isActive && <span className="thinking-dots"><span>.</span><span>.</span><span>.</span></span>}
                {elapsedMs !== null && <span className="thinking-stat">{formatDuration(elapsedMs)}</span>}
                {thoughtChars > 0 && <span className="thinking-stat">{thoughtChars} chars</span>}
                {text && !isActive && (
                    <span className="thinking-toggle">{expanded ? '▾' : '▸'}</span>
                )}
            </div>
            {text && (expanded || isActive) && (
                <div className="thinking-content">
                    {text}
                </div>
            )}
        </div>
    )
}
